import React, { useEffect, useState } from "react";
import { IEvent, IShow, IUkcAgilityAwards } from "@eagerdog/interfaces";
import { calculateUkcAgilityAwards } from "@eagerdog/awards-util";
import { apiService } from "../../../services/api.service";
import { toast } from "../../Toast/ToastManager";

import "./UkcAgilityAwards.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  scores?: any[];
}

const UkcAgilityAwards: React.FC<IProps> = ({ event, show, scores: providedScores }) => {
  const [loading, setLoading] = useState(!providedScores);
  const [awards, setAwards] = useState<IUkcAgilityAwards | null>(null);

  useEffect(() => {
    loadScoresAndCalculateAwards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, providedScores]);

  const loadScoresAndCalculateAwards = async () => {
    try {
      setLoading(true);
      const scores = providedScores || await apiService.getEventScores(event._id.toString());

      const showScores = scores.filter(
        (score) =>
          score.show_type === show.show_type &&
          score.show_id.toString() === show._id.toString()
      );

      setAwards(calculateUkcAgilityAwards(showScores, show));
    } catch (_error) {
      toast.show({
        title: "Error",
        content: "Failed to load scores for awards calculation",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="UkcAgilityAwards loading">Loading awards...</div>;
  }

  if (!awards) {
    return <div className="UkcAgilityAwards error">Failed to calculate awards</div>;
  }

  const renderAward = (
    title: string,
    award: IUkcAgilityAwards["highInTrial"] | IUkcAgilityAwards["highScoringJunior"]
  ) => {
    return (
      <div className="awardSection">
        <h4>{title}</h4>
        {!award || award.winners.length === 0 ? (
          <p className="noWinner">No qualifying scores for this award</p>
        ) : (
          <>
            {award.winners.map((winner) => (
              <p key={`${title}-${winner.arm_number}-${winner.level}`} className="awardWinner">
                #{winner.arm_number} {winner.registered_name} ({winner.breed}) - {winner.level} -
                Score: {winner.score} - Time: {winner.running_time} - ACT: {winner.normal_sct} - Time under ACT: {winner.time_under_sct}
              </p>
            ))}
            {award.is_tie && (
              <p className="awardDetail">Tie stands per UKC rules.</p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="UkcAgilityAwards">
      {renderAward("High in Trial", awards.highInTrial)}
      {renderAward("High Scoring Junior", awards.highScoringJunior)}
    </div>
  );
};

export default UkcAgilityAwards;
